import { RSAA } from 'redux-api-middleware';
import customFailure from 'helpers/customFailureAction';
import { MAX_LIMIT } from 'config';
import { Dispatch } from 'redux';
import { TGetRootState, TDispatchResult } from 'types/shared';
import { ChartTypes, CountryIds } from 'types/shared';

export const REQUEST = 'opus/charts/REQUEST';
export const SUCCESS = 'opus/charts/SUCCESS';
export const FAILURE = 'opus/charts/FAILURE';

type ChartActionTypes = typeof REQUEST | typeof SUCCESS | typeof FAILURE;

export interface IChartData {
  id: number;
  chartName: string;
  chartDisplayName: string;
  customerId: number;
  customerName: string;
  updateFrequency: string;
  chartType: ChartTypes;
  numberOfSupportedCountries: number;
  supportedCountryIds: CountryIds[];
}

interface IChartState {
  loading: boolean;
  success: boolean;
  error: Error | null;
  errorInfo: any;
  data: any;
}

interface IChartAction {
  type: ChartActionTypes;
  payload: any;
}

export const initialState: IChartState = {
  loading: false,
  success: false,
  error: null,
  errorInfo: null,
  data: {},
};

export default function reducer(
  state: IChartState = initialState,
  action: IChartAction
): IChartState {
  switch (action.type) {
    case REQUEST:
      return {
        ...state,
        loading: true,
      };
    case SUCCESS:
      return {
        ...state,
        loading: false,
        success: true,
        error: null,
        errorInfo: null,
        data: {
          ...state.data,
          ...action.payload,
        },
      };
    case FAILURE:
      return {
        ...state,
        loading: false,
        success: false,
        error: new Error(action.payload.error),
        errorInfo: action.payload.errorInfo,
      };

    default:
      return state;
  }
}

/**
 * Load charts data from the '/api/charts/list' endpoint.
 * The default payload from 'redux-api-middleware' is modified so it matches
 * the way we handle errors in react-refetch's custom apiConnect.js.
 *
 * @see https://github.com/agraboso/redux-api-middleware
 * @param
 */
export const loadCharts = (limit = MAX_LIMIT): any => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { charts } = getState();
    return await dispatch({
      [RSAA]: {
        bailout: charts.loading || charts.success,
        types: [
          REQUEST,
          SUCCESS,
          {
            type: FAILURE,
            payload: async (
              action: IChartAction,
              state: IChartState,
              res: any
            ): Promise<any> => await customFailure(res),
          },
        ],
        endpoint: `/api/charts/list?sortBy=chartDisplayName&sortOrder=asc&limit=${limit}`,
        method: 'GET',
        credentials: 'same-origin',
      },
    });
  };
};

export function shouldLoadCharts(): TDispatchResult<boolean> {
  return (_: Dispatch, getState: TGetRootState): boolean => {
    const {
      charts: { loading, success },
    } = getState();

    // If the data didn't load before AND the data isn't loading currently, we
    // should probably try to load the data.
    return !success && !loading;
  };
}
